import type {ReactNode} from 'react';
import type {AiChatSidebarProps} from './ai-chat-sidebar.types';
import styles from './ai-chat-sidebar.module.scss';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import {Text, TextSize} from '@Components/text';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {GA4EventName, trackGA4CustomEvent} from '@Libraries/ga-events';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {SkeletonBrickTheme} from '@Components/skeleton-brick';
import {Thumbnail} from '@Components/thumbnail';
import {getAssetUrl} from '@Utils/s3.util';
import {onLogoClick} from '@Libraries/ai-chat.library';
import {ClickableDiv} from '@Components/clickable-div';
import {MiddleClickNewTab} from '@Components/middle-click-new-tab';
import {getAiChatBasePath} from '@/ai-chat/lib/chat-history-library';

export function AiChatSidebar({...props}: AiChatSidebarProps): ReactNode {
  const {state, actions} = useAiChat();

  if (state.isSidebarExpanded) {
    return null;
  }

  const getTooltip = (title: string): OverlayChildren => {
    return (
      <div className={`spacing-p-t-1 spacing-p-b-1 spacing-p-l-2 spacing-p-r-2 radius-8 ${styles.tooltipContainer}`}>
        <Text size={TextSize.XSMALL} className={styles.tooltipText} val={title} />
      </div>
    );
  };

  const getLogo = (): ReactNode => {
    return (
      <ClickableDiv
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Thumbnail
          data={{
            type: ThumbnailType.IMAGE,
            imageSrc: getAssetUrl('pmw-logo-small-dark.png'),
            showSkeletonLoading: true,
            skeletonLoadingTheme: SkeletonBrickTheme.LIGHT,
          }}
          className={`spacing-p-2 _cursor-pointer radius-8 ${styles.sidebarLogo}`}
          onClick={onLogoClick}
        />
      </ClickableDiv>
    );
  };

  const getHistoryDrawerTrigger = (): ReactNode => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_open_sidebar'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 8]}
      >
        <Icon
          className={`radius-8`}
          onClick={(e) => {
            e.stopPropagation();
            trackGA4CustomEvent(GA4EventName.AICHAT_ALL_CHATS);
            actions.setIsSidebarExpanded(true);
          }}
          icon="icon-sidebar"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.TRANSPARENT}
          isUsedAsButton
        />
      </OverlayTrigger>
    );
  };

  const getNewChatTrigger = (): ReactNode => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_new_chat'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 8]}
      >
        <MiddleClickNewTab href={getAiChatBasePath()}>
          <Icon
            className={`radius-8`}
            onClick={(e) => {
              e.stopPropagation();
              actions.activateForNewChat();
              actions.setCurrentChatId(undefined);
            }}
            icon="icon-plus"
            size={IconSize.SIZE_ICON_20}
            shape={IconShape.SQUARE}
            type={IconType.TRANSPARENT}
            isUsedAsButton
          />
        </MiddleClickNewTab>
      </OverlayTrigger>
    );
  };

  const getSearchTrigger = (): ReactNode => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_search'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 8]}
      >
        <Icon
          className={`radius-8`}
          onClick={(e) => {
            e.stopPropagation();
            actions.setIsSidebarExpanded(true);
            actions.setShouldFocusSearch(true);
          }}
          icon="icon-search"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={IconType.TRANSPARENT}
          isUsedAsButton
        />
      </OverlayTrigger>
    );
  };

  const getQuickTipsTrigger = (): ReactNode => {
    return (
      <OverlayTrigger
        defaultShow={false}
        trigger={['hover', 'focus']}
        overlay={getTooltip(window.i18next.t('pmwjs_quick_tips'))}
        delay={{show: 100, hide: 100}}
        placement="right"
        offset={[0, 8]}
      >
        <Icon
          className={state.isQuickTipsView ? 'radius-4' : 'radius-8'}
          onClick={(e) => {
            e.stopPropagation();
            actions.navigateToQuickTips();
          }}
          icon="icon-bulb"
          size={IconSize.SIZE_ICON_20}
          shape={IconShape.SQUARE}
          type={state.isQuickTipsView ? IconType.FLAT : IconType.SECONDARY}
          flatIconType={state.isQuickTipsView ? {color: FlatIconColor.PRIMARY, type: FlatIconColorType.DEFAULT} : undefined}
          isUsedAsButton
        />
      </OverlayTrigger>
    );
  };

  const getTopOptions = (): ReactNode => {
    return (
      <div className={'flex-v-row gap-2 '}>
        {getLogo()}
        {getHistoryDrawerTrigger()}
        {getNewChatTrigger()}
        {getSearchTrigger()}
      </div>
    );
  };

  return (
    <ClickableDiv
      className={`_full-height spacing-p-1 spacing-p-b-2 flex-v-row flex-justify-between _cursor-pointer ${styles.sidebarContainer} ${props.className ?? ''}`}
      onClick={() => {
        trackGA4CustomEvent(GA4EventName.AICHAT_ALL_CHATS);
        actions.setIsSidebarExpanded(true);
      }}
    >
      {getTopOptions()}
      {getQuickTipsTrigger()}
    </ClickableDiv>
  );
}
